import { useRouter } from 'next/router'

import parseRecordId from '@/common/utils/number/parseRecordId'

import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { type Query, SQL, type SQLFragment } from '@/client/query'

export function useUserFromQuery(): { user: Query; userIdQuery: SQLFragment<number> } {
  const query = useQuery()
  const router = useRouter()

  const userIdentifier = `${router.query.user}`
  const maybeUserId = parseRecordId(userIdentifier)

  const userWhere =
    maybeUserId == null
      ? UserQ.where({ username: userIdentifier })
      : UserQ.where({ id: maybeUserId })

  const userIdQuery = SQL<number>`(SELECT ${UserQ.id} ${userWhere})`

  const user = query.one(userWhere)

  return { user, userIdQuery }
}

export function useUserUpcomingSeries(): Query[] {
  const query = useQuery()
  const currentUser = useUser()

  const { userIdQuery } = useUserFromQuery()

  const upcomingSeries = query.many`${SeriesQ.where(
    {
      includeAllResearchGroups: true,
      include: [{ association: 'Course', required: true }],
      hideGatedSeries: true,
    },
    SQL`
      ${SeriesQ.userCanViewSeries(currentUser.id, { disableBucketCheck: currentUser.isLoggedOut })}
      AND ${SeriesQ.userMatchesSeriesResearchGroup(currentUser)}
      AND ${SeriesQ.isHost({ userId: userIdQuery })}
    `
  )}
    AND EXISTS(SELECT 1 ${SessionQ.where(
      {
        seriesId: SeriesQ.id,
      },
      SQL`${SessionQ.getComputedEndDate()} > CURRENT_TIMESTAMP`
    )})
    ORDER BY (SELECT ${SessionQ.start_date} ${SeriesQ.getNextSession()}) ASC`

  return upcomingSeries
}
